<template>
    <div class="animated fadeIn">
        <ComponentHeader
            title="Leads Management"
            :hasSearch="true"
            :hasCreate="true"
            :hasReload="true"
            :hasback="false"
            createButtonText="Add Lead"
            @search-query="(val) => (searchQuery = val)"
            @create-press="showAddLeadModal"
            @reload-data="refreshLeads"
            :isLoading="isLoading"
        >
            <template #heading-before-search>
                <div class="leads-filters-wrapper mr-2">
                    <b-form-select
                        v-model="selectedSource"
                        :options="sourceOptions"
                        class="leads-filter-select"
                    />
                    
                    <b-form-select
                        v-model="selectedStatus"
                        :options="statusOptions"
                        class="leads-filter-select"
                    />
                </div>
            </template>
        </ComponentHeader> 
        <!-- Stats Cards -->
        <b-row class="mt-3">
            <b-col cols="12" md="3" class="mb-3 mb-md-0">
                <b-card class="card-wrap stats-card">
                    <div class="stats-label">Total Leads</div>
                    <div class="stats-value">{{ stats.totalLeads }}</div>
                </b-card>
            </b-col>
            <b-col cols="12" md="3" class="mb-3 mb-md-0">
                <b-card class="card-wrap stats-card">
                    <div class="stats-label">New</div>
                    <div class="stats-value">{{ stats.new }}</div>
                </b-card>
            </b-col>
            <b-col cols="12" md="3" class="mb-3 mb-md-0">
                <b-card class="card-wrap stats-card">
                    <div class="stats-label">Qualified</div>
                    <div class="stats-value">{{ stats.qualified }}</div>
                </b-card>
            </b-col>
            <b-col cols="12" md="3" class="mb-3 mb-md-0">
                <b-card class="card-wrap stats-card">
                    <div class="stats-label">Conversion Rate</div>
                    <div class="stats-value">{{ stats.conversionRate }}%</div>
                </b-card>
            </b-col>
        </b-row>

        <!-- Leads Table -->
        <b-row>
            <b-col cols="12" md="12" class="leads-table-col">
                <b-card
                    class="card-wrap leads-table-card"
                    border-variant="info"
                >
                    <div class="table-wrapper">
                        <b-table
                            id="leads-table"
                            class="leads-table leads-table-compact"
                            responsive="sm"
                            :items="filteredLeads"
                            :fields="tableFields"
                            :current-page="currentPage"
                            :per-page="perPage"
                            :busy="isLoading"
                            :bordered="true"
                            :striped="false"
                            thead-class="header"
                            show-empty
                            hover
                        >
                            <template slot="cell(leadName)" slot-scope="data">
                                <div class="lead-name-cell">
                                    <div class="lead-avatar">
                                        {{ getInitials(data.item.name) }}
                                    </div>
                                    <div class="lead-info">
                                        <div class="font-weight-semibold">
                                            {{ data.item.name }}
                                        </div>
                                        <div class="text-muted small">
                                            {{ data.item.jobTitle }}
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <template slot="cell(company)" slot-scope="data">
                                {{
                                    (data && data.item && data.item.company) ||
                                    "—"
                                }}
                            </template>

                            <template slot="cell(contact)" slot-scope="data">
                                <div>
                                    <div>{{ data.item.email }}</div>
                                    <div class="text-muted small">
                                        {{
                                            (data &&
                                                data.item &&
                                                data.item.phone) ||
                                            "—"
                                        }}
                                    </div>
                                </div>
                            </template>

                            <template slot="cell(source)" slot-scope="data">
                                <span
                                    class="status-badge"
                                    :class="
                                        getSourceBadgeClass(data.item.source)
                                    "
                                >
                                    {{ formatSourceDisplay(data.item.source) }}
                                </span>
                            </template>

                            <template slot="cell(owner)" slot-scope="data">
                                {{ data.item.owner }}
                            </template>

                            <template slot="cell(status)" slot-scope="data">
                                <span
                                    class="status-badge"
                                    :class="
                                        getStatusBadgeClass(data.item.status)
                                    "
                                >
                                    {{ formatStatusDisplay(data.item.status) }}
                                </span>
                            </template>

                            <template slot="cell(created)" slot-scope="data">
                                {{ formatDate(data.item.created) }}
                            </template>

                            <template slot="cell(actions)" slot-scope="data">
                                <div class="action-btn-group">
                                    <b-button
                                        size="sm"
                                        class="button-circle"
                                        variant="primary"
                                        @click="viewLeads(data.item)"
                                        title="View Lead"
                                    >
                                        <i class="fa fa-eye"></i>
                                    </b-button>

                                    <b-button
                                        size="sm"
                                        class="button-circle btn-orange"
                                        variant="primary"
                                        @click="editLeads(data.item)"
                                        title="Edit Lead"
                                    >
                                        <i class="fa fa-pencil edit"></i>
                                    </b-button>

                                    <b-button
                                        size="sm"
                                        class="button-circle btn-green"
                                        variant="success"
                                        @click="convertToContact(data.item)"
                                        title="Convert to Contact"
                                    >
                                        <i class="fa fa-user-plus"></i>
                                    </b-button>

                                    <b-button
                                        size="sm"
                                        class="button-circle"
                                        variant="danger"
                                        @click="deleteLead(data.item)"
                                        title="Delete Lead"
                                    >
                                        <i class="fa fa-trash"></i>
                                    </b-button>
                                </div>
                            </template>
                            <template slot="table-busy">
                                <div class="text-center py-4">
                                    <b-spinner class="align-middle"></b-spinner>
                                    <p class="mt-2 mb-0">Loading leads...</p>
                                </div>
                            </template>
                            <template slot="empty">
                                <div class="text-center py-4">
                                    <p>No leads found.</p>
                                </div>
                            </template>
                        </b-table>
                    </div>
                    <div class="pagination-wrapper">
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            aria-controls="leads-table"
                            prev-text="Prev"
                            next-text="Next"
                            hide-goto-end-buttons
                            class="mt-3"
                        ></b-pagination>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- Add Lead Modal -->
        <b-modal
            id="addLeadModal"
            ref="addLeadModal"
            size="lg"
            centered
            :title="isEditMode ? 'Edit Lead' : 'Add New Lead'"
            hide-footer
            modal-class="center-title-modal add-lead-modal"
            @hidden="resetModal"
        >
            <p class="modal-subtitle">
                {{
                    isEditMode
                        ? "Update the lead information below."
                        : "Enter lead details manually or upload a CSV/Excel file to import multiple leads."
                }}
            </p>

            <div class="modal-tabs-wrapper" v-if="!isEditMode">
                <b-button
                    :variant="
                        activeTab === 0 ? 'secondary' : 'outline-secondary'
                    "
                    :class="['tab-button', { active: activeTab === 0 }]"
                    @click="activeTab = 0"
                    title="Manual Entry"
                >
                    Manual Entry
                </b-button>
                <b-button
                    :variant="
                        activeTab === 1 ? 'secondary' : 'outline-secondary'
                    "
                    :class="['tab-button', { active: activeTab === 1 }]"
                    @click="activeTab = 1"
                    disabled
                    title="Upload CSV/Excel"
                >
                    Upload CSV/Excel
                </b-button>
            </div>

            <div class="modal-content-wrapper">
                <div v-if="activeTab === 0 || isEditMode" class="tab-content">
                    <b-row>
                        <b-col cols="12" md="6">
                            <b-form-group label="Name *" label-for="leadName">
                                <b-form-input
                                    id="leadName"
                                    v-model="leadForm.name"
                                    placeholder="e.g., John Doe"
                                    required
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="6">
                            <b-form-group label="Email *" label-for="leadEmail">
                                <b-form-input
                                    id="leadEmail"
                                    v-model="leadForm.email"
                                    type="email"
                                    placeholder="e.g., john.doe@example.com"
                                    required
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col cols="12" md="6">
                            <b-form-group
                                label="Company"
                                label-for="leadCompany"
                            >
                                <b-form-input
                                    id="leadCompany"
                                    v-model="leadForm.company"
                                    placeholder="e.g., TechCorp"
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="6">
                            <b-form-group label="Phone" label-for="leadPhone">
                                <b-form-input
                                    id="leadPhone"
                                    v-model="leadForm.phone"
                                    placeholder="e.g., +1 555-123-4567"
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col cols="12" md="6">
                            <b-form-group
                                label="Job Title"
                                label-for="leadJobTitle"
                            >
                                <b-form-input
                                    id="leadJobTitle"
                                    v-model="leadForm.jobTitle"
                                    placeholder="e.g., Sales Manager"
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="6">
                            <b-form-group label="City" label-for="leadCity">
                                <b-form-input
                                    id="leadCity"
                                    v-model="leadForm.city"
                                    placeholder="e.g., San Francisco"
                                    :disabled="isEditMode"
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col cols="12" md="6">
                            <b-form-group label="Source" label-for="leadSource">
                                <b-form-select
                                    id="leadSource"
                                    v-model="leadForm.source"
                                    :options="sourceSelectOptions"
                                ></b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="6">
                            <b-form-group
                                label="Assigned Owner"
                                label-for="leadOwner"
                            >
                                <b-form-input
                                    id="leadOwner"
                                    v-model="userName"
                                    disabled
                                    placeholder="e.g., John Doe"
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col cols="12">
                            <b-form-group
                                label="Subject"
                                label-for="leadSubject"
                            >
                                <b-form-input
                                    id="leadSubject"
                                    v-model="leadForm.subject"
                                    placeholder="e.g., Inquiry about services"
                                ></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col cols="12">
                            <b-form-group
                                label="Message"
                                label-for="leadMessage"
                            >
                                <b-form-textarea
                                    id="leadMessage"
                                    v-model="leadForm.message"
                                    placeholder="Enter message details..."
                                    rows="4"
                                ></b-form-textarea>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-row v-if="isEditMode">
                        <b-col cols="12" md="6">
                            <b-form-group label="Status" label-for="leadStatus">
                                <b-form-select
                                    id="leadStatus"
                                    v-model="leadForm.status"
                                    :options="statusSelectOptions"
                                ></b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>

                <div v-if="activeTab === 1" class="tab-content">
                    <b-form-group label="Upload File" label-for="fileUpload">
                        <b-form-file
                            id="fileUpload"
                            v-model="uploadedFile"
                            accept=".csv,.xlsx,.xls"
                            placeholder="Choose a file or drop it here..."
                            drop-placeholder="Drop file here..."
                        ></b-form-file>
                        <small class="form-text text-muted">
                            Accepted formats: CSV, XLSX, XLS
                        </small>
                    </b-form-group>
                </div>
            </div>

            <div class="modal-actions">
                <b-button
                    variant="outline-primary"
                    @click="$refs.addLeadModal.hide()"
                    title="Cancel"
                >
                    Cancel
                </b-button>
                <b-button
                    variant="primary"
                    :disabled="isLoading"
                    @click="handleAddLead"
                    :title="isEditMode ? 'Update Lead' : 'Add New Lead'"
                >
                    <span
                        v-if="isLoading"
                        class="fa fa-refresh fa-spin mr-1"
                    ></span>
                    <i
                        v-else
                        :class="isEditMode ? 'fa fa-save' : 'fa fa-plus'"
                        class="mr-1"
                    ></i>
                    {{ isEditMode ? "Update Lead" : "Add Lead" }}
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import ComponentHeader from "@/components/componentNewHeader";

const SOURCE_TO_API = {
    contact_form: "CONTACT_FORM",
    website: "WEBSITE",
    campaign: "CAMPAIGN",
    manual: "MANUAL",
    referral: "REFERRAL",
    social: "SOCIAL",
};
const API_TO_SOURCE = {
    CONTACT_FORM: "contact_form",
    REGISTRATION: "registration",
    WEBSITE: "website",
    CAMPAIGN: "campaign",
    SOCIAL: "social",
    MANUAL: "manual",
};
const STATUS_TO_API = {
    new: "NEW",
    contacted: "CONTACTED",
    qualified: "QUALIFIED",
    lost: "LOST",
    converted: "CONVERTED",
    fraud_suspected: "FRAUD_SUSPECTED",
    fraud_confirmed: "FRAUD_CONFIRMED",
    valid: "VALID",
    invalid: "INVALID",
    duplicate: "DUPLICATE",
    blacklisted: "BLACKLISTED",
};
const API_TO_STATUS = {
    NEW: "new",
    CONTACTED: "contacted",
    QUALIFIED: "qualified",
    LOST: "lost",
    CONVERTED: "converted",
    FRAUD_SUSPECTED: "fraud_suspected",
    FRAUD_CONFIRMED: "fraud_confirmed",
    VALID: "valid",
    INVALID: "invalid",
    DUPLICATE: "duplicate",
    BLACKLISTED: "blacklisted",
};

function getEmptyLeadForm() {
    return {
        name: "",
        email: "",
        company: "",
        phone: "",
        jobTitle: "",
        city: "",
        source: "manual",
        status: "new",
        owner: "John Doe",
        subject: "",
        message: "",
    };
}

export default {
    name: "LeadsManagement",
    components: {
        ComponentHeader,
    },
    data() {
        return {
            searchQuery: "",
            selectedSource: "all",
            selectedStatus: "all",
            currentPage: 1,
            perPage: 10,
            activeTab: 0,
            uploadedFile: null,
            isEditMode: false,
            editingLeadIndex: null,
            editingLeadId: null,
            isLoading: false,
            leadForm: getEmptyLeadForm(),
            stats: {
                totalLeads: 0,
                new: 0,
                qualified: 0,
                conversionRate: 0,
            },
            leads: [],
            tableFields: [
                { key: "leadName", label: "Lead Name", sortable: true },
                { key: "company", label: "Company", sortable: true },
                { key: "contact", label: "Contact", sortable: false },
                { key: "source", label: "Source", sortable: true },
                { key: "owner", label: "Owner", sortable: true },
                { key: "status", label: "Status", sortable: true },
                { key: "created", label: "Created", sortable: true },
                {
                    key: "actions",
                    label: "Actions",
                    sortable: false,
                    class: "text-center",
                },
            ],
            sourceOptions: [
                { value: "all", text: "All Sources" },
                { value: "contact_form", text: "Contact Form" },
                { value: "registration", text: "Registration" },
                { value: "website", text: "Website" },
                { value: "campaign", text: "Campaign" },
                { value: "manual", text: "Manual" },
                { value: "social", text: "Social" },
            ],
            statusOptions: [
                { value: "all", text: "All Statuses" },
                { value: "new", text: "New" },
                { value: "contacted", text: "Contacted" },
                { value: "qualified", text: "Qualified" },
                { value: "lost", text: "Lost" },
                { value: "converted", text: "Converted" },
                { value: "fraud_suspected", text: "Fraud Suspected" },
                { value: "fraud_confirmed", text: "Fraud Confirmed" },
                { value: "valid", text: "Valid" },
                { value: "invalid", text: "Invalid" },
                { value: "duplicate", text: "Duplicate" },
                { value: "blacklisted", text: "Blacklisted" },
            ],
            sourceSelectOptions: [
                { value: "contact_form", text: "Contact Form" },
                { value: "social", text: "Social" },
                { value: "manual", text: "Manual" },
            ],
            statusSelectOptions: [
                { value: "new", text: "New" },
                { value: "contacted", text: "Contacted" },
                { value: "qualified", text: "Qualified" },
                { value: "lost", text: "Lost" },
                { value: "converted", text: "Converted" },
                { value: "fraud_suspected", text: "Fraud Suspected" },
                { value: "fraud_confirmed", text: "Fraud Confirmed" },
                { value: "valid", text: "Valid" },
                { value: "invalid", text: "Invalid" },
                { value: "duplicate", text: "Duplicate" },
                { value: "blacklisted", text: "Blacklisted" },
            ],
            ownerOptions: [
                { value: "John Doe", text: "John Doe" },
                { value: "Jane Smith", text: "Jane Smith" },
            ],
        };
    },
    computed: {
        filteredLeads() {
            let filtered = this.leads;
            console.log("Filtering leads with query:", this.leads);

            // Filter by search query
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter(
                    (lead) =>
                        lead.name?.toLowerCase().includes(query) ||
                        lead.company?.toLowerCase().includes(query) ||
                        lead.email?.toLowerCase().includes(query) ||
                        lead.phone?.toString().includes(query),
                );
            }

            // Filter by source
            if (this.selectedSource !== "all") {
                filtered = filtered.filter(
                    (lead) => lead.source === this.selectedSource,
                );
            }

            // Filter by status
            if (this.selectedStatus !== "all") {
                filtered = filtered.filter(
                    (lead) => lead.status === this.selectedStatus,
                );
            }

            return filtered;
        },
        totalRows() {
            return this.filteredLeads.length;
        },
        tenantId() {
            try {
                const user = JSON.parse(
                    sessionStorage.getItem("userLogin") || "null",
                );
                return user?._id || null;
            } catch {
                return null;
            }
        },
        userName() {
            try {
                const user = JSON.parse(
                    sessionStorage.getItem("userLogin") || "null",
                );
                return user?.userName || "";
            } catch {
                return "";
            }
        },
        filterPayload() {
            return this.tenantId;
        },
    },
    watch: {
        selectedSource() {
            this.currentPage = 1;
        },
        selectedStatus() {
            this.currentPage = 1;
        },
    },
    methods: {
        async fetchLeads() {
            if (!this.tenantId) {
                this.$toasted?.error("Session expired. Please log in again.");
                return;
            }
            this.isLoading = true;
            try {
                const leadsData = await this.$store.dispatch(
                    "channel/fetchLeads",
                    this.tenantId,
                );
                if (leadsData && Array.isArray(leadsData)) {
                    this.leads = leadsData.map((lead) => {
                        const source =
                            API_TO_SOURCE[lead.source] ||
                            lead.source?.toLowerCase() ||
                            "manual";
                        const status =
                            API_TO_STATUS[lead.status] ||
                            lead.status?.toLowerCase() ||
                            "new";
                        return {
                            _id: lead._id,
                            name: lead.personal?.name || "",
                            email: lead.personal?.email || "",
                            phone: lead.personal?.phone || "",
                            company: lead.company?.name || "",
                            jobTitle: lead.company?.title || "",
                            source,
                            originalSource: lead.source || "",
                            status,
                            created: lead.createdAt || "",
                            createdAt: lead.createdAt || "",
                            owner: this.userName,
                            isConverted: lead.isConverted || false,
                            contactId: lead.contactId || null,
                            subject: lead.subject || "",
                            message: lead.message || "",
                        };
                    });
                    this.calculateStats();
                }
            } catch (error) {
                const msg =
                    error?.response?.data?.message ||
                    error?.message ||
                    "Failed to fetch leads. Please try again.";
                this.$toasted?.error(msg);
            } finally {
                this.isLoading = false;
            }
        },
        calculateStats() {
            const totalLeads = this.leads.length;
            const newLeads = this.leads.filter(
                (lead) => lead.status === "new",
            ).length;
            const qualifiedLeads = this.leads.filter(
                (lead) => lead.status === "qualified",
            ).length;
            const convertedLeads = this.leads.filter(
                (lead) => lead.status === "converted" || lead.isConverted,
            ).length;
            const conversionRate =
                totalLeads > 0
                    ? Math.round((convertedLeads / totalLeads) * 100)
                    : 0;

            this.stats = {
                totalLeads: totalLeads,
                new: newLeads,
                qualified: qualifiedLeads,
                conversionRate: conversionRate,
            };
        },
        showAddLeadModal() {
            this.$refs.addLeadModal.show();
        },
        resetModal() {
            this.activeTab = 0;
            this.uploadedFile = null;
            this.isEditMode = false;
            this.editingLeadIndex = null;
            this.editingLeadId = null;
            this.leadForm = getEmptyLeadForm();
        },
        refreshLeads() {
            this.fetchLeads();
        },
        async handleAddLead() {
            if (!this.tenantId) {
                this.$toasted?.error("Session expired. Please log in again.");
                return;
            }
            if (this.isEditMode) {
                if (!this.editingLeadId) {
                    this.$toasted?.error("Lead ID not found.");
                    return;
                }

                const apiSource =
                    SOURCE_TO_API[this.leadForm.source] || "MANUAL";
                const apiStatus = STATUS_TO_API[this.leadForm.status] || "NEW";

                const payload = {
                    id: this.editingLeadId,
                    source: apiSource,
                    status: apiStatus,
                    subject: this.leadForm.subject || "",
                    message: this.leadForm.message || "",
                    personal: {
                        name: this.leadForm.name || "",
                        email: this.leadForm.email || "",
                        phone: this.leadForm.phone || "",
                    },
                    company: {
                        name: this.leadForm.company || "",
                        title: this.leadForm.jobTitle || "",
                    },
                };

                this.isLoading = true;
                try {
                    const response = await this.$store.dispatch(
                        "channel/editLead",
                        payload,
                    );
                    if (response?.status === 0) {
                        this.$toasted?.error(
                            response.message || "Lead update failed",
                        );
                        return;
                    }
                    this.$refs.addLeadModal.hide();
                    this.$toasted?.success("Lead updated successfully!");
                    await this.fetchLeads();
                } catch (error) {
                    const msg =
                        error?.response?.data?.message ||
                        error?.message ||
                        "Failed to update lead. Please try again.";
                    this.$toasted?.error(msg);
                } finally {
                    this.isLoading = false;
                }
            } else if (this.activeTab === 0) {
                // Manual entry - Add new lead
                if (!this.leadForm.name || !this.leadForm.email) {
                    this.$toasted?.error("Name and Email are required fields.");
                    return;
                }

                const apiSource =
                    SOURCE_TO_API[this.leadForm.source] || "MANUAL";

                const payload = {
                    source: apiSource,
                    tenant: this.tenantId,
                    personal: {
                        name: this.leadForm.name,
                        email: this.leadForm.email,
                        phone: this.leadForm.phone || "",
                    },
                    company: {
                        name: this.leadForm.company || "",
                        title: this.leadForm.jobTitle || "",
                    },
                    ownerId: this.tenantId,
                    subject: this.leadForm.subject || "",
                    message: this.leadForm.message || "",
                };

                this.isLoading = true;
                try {
                    const response = await this.$store.dispatch(
                        "channel/addLead",
                        payload,
                    );
                    if (response?.status === 0) {
                        this.$toasted?.error(
                            response.message || "Lead validation failed",
                        );
                        return;
                    }
                    this.$refs.addLeadModal.hide();
                    this.$toasted?.success("Lead added successfully!");
                    await this.fetchLeads();
                } catch (error) {
                    const msg =
                        error?.response?.data?.message ||
                        error?.message ||
                        "Failed to add lead. Please try again.";
                    this.$toasted?.error(msg);
                } finally {
                    this.isLoading = false;
                }
            } else {
                // CSV/Excel upload
                if (!this.uploadedFile) {
                    this.$toasted?.error("Please select a file to upload.");
                    return;
                }
                // Upload logic here
                this.$toasted?.success("File uploaded successfully!");
                this.$refs.addLeadModal.hide();
            }
        },
        formatSourceDisplay(source) {
            if (!source) return "";
            return source
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");
        },
        formatStatusDisplay(status) {
            if (!status) return "";
            return status
                .toString()
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");
        },
        getSourceBadgeClass(source) {
            const classes = {
                contact_form: "badge-info",
                social: "badge-primary",
                website: "badge-primary",
                campaign: "badge-info",
                manual: "badge-secondary",
                referral: "badge-warning",
            };
            return classes[source] || "badge-secondary";
        },
        getStatusBadgeClass(status) {
            const classes = {
                new: "badge-info",
                contacted: "badge-warning",
                qualified: "badge-success",
                lost: "badge-secondary",
                converted: "badge-success",
                fraud_suspected: "badge-warning",
                fraud_confirmed: "badge-danger",
                valid: "badge-success",
                invalid: "badge-danger",
                duplicate: "badge-warning",
                blacklisted: "badge-danger",
            };
            return classes[status] || "badge-secondary";
        },
        formatDate(dateString) {
            if (!dateString) return "";
            try {
                const date = new Date(dateString);
                return date
                    .toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                    })
                    .replace(",", "");
            } catch (error) {
                return dateString;
            }
        },
        getInitials(name) {
            if (!name) return "";
            const parts = name.trim().split(" ");
            if (parts.length === 1) {
                return parts[0].charAt(0).toUpperCase();
            }
            return (
                parts[0].charAt(0).toUpperCase() +
                parts[parts.length - 1].charAt(0).toUpperCase()
            );
        },
        viewLeads(lead) {
            // Pass lead ID to view page
            this.$router.push({
                name: "Leads Details",
                params: { id: lead._id },
            });
        },
        editLeads(lead) {
            // Find the lead index in the array
            const leadIndex = this.leads.findIndex((l) => l._id === lead._id);

            if (leadIndex !== -1) {
                this.isEditMode = true;
                this.editingLeadIndex = leadIndex;
                this.editingLeadId = lead._id;
                this.activeTab = 0; // Set to manual entry tab

                this.leadForm = {
                    ...getEmptyLeadForm(),
                    name: lead.name || "",
                    email: lead.email || "",
                    company: lead.company || "",
                    phone: lead.phone || "",
                    jobTitle: lead.jobTitle || "",
                    city: lead.city || "",
                    source: lead.source || "manual",
                    status: lead.status || "new",
                    owner: lead.owner || this.userName,
                    subject: lead.subject || "",
                    message: lead.message || "",
                };

                // Show the modal
                this.$refs.addLeadModal.show();
            } else {
                this.$toasted?.error("Lead not found.");
            }
        },
        async convertToContact(lead) {
            if (lead.isConverted) {
                this.$toasted?.info("This lead has already been converted.");
                return;
            }
            try {
                const payload = {
                    LeadId: lead._id,
                    tenant: this.tenantId,
                };
                await this.$store.dispatch(
                    "channel/convertLeadAction",
                    payload,
                );
                this.$toasted?.success("Lead converted successfully.");
                await this.fetchLeads();
            } catch (error) {
                const msg =
                    error?.response?.data?.message ||
                    error?.message ||
                    "Failed to convert lead.";
                this.$toasted?.error(msg);
            }
        },
        deleteLead(lead) {
            this.$swal({
                title: "Delete this lead?",
                text: "Are you sure? You won't be able to revert this!",
                type: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, delete it!",
                cancelButtonText: "Cancel",
                reverseButtons: true,
            }).then(async (result) => {
                if (result.value) {
                    try {
                        await this.$store.dispatch("channel/deleteLeadAction", {
                            id: lead._id,
                            tenant: this.tenantId,
                            data: {},
                        });
                        await this.fetchLeads();

                        this.$swal({
                            title: "Deleted!",
                            text: "Lead has been deleted successfully.",
                            type: "success",
                            timer: 1500,
                            showConfirmButton: false,
                        });
                    } catch (error) {
                        const msg =
                            error?.response?.data?.message ||
                            error?.message ||
                            "Failed to delete lead.";
                        this.$swal({
                            title: "Error",
                            text: msg,
                            type: "error",
                        });
                    }
                }
            });
        },
    },
    mounted() {
        this.fetchLeads();
    },
};
</script>

<style scoped>
.leads-page {
    width: 90vw;
    max-width: 100%;
    margin: 0 auto;
}

@media (max-width: 991.98px) {
    .leads-page {
        width: 95vw;
    }
}

@media (max-width: 767.98px) {
    .leads-page {
        width: 100%;
    }
}

.card-wrap {
    box-shadow: 0 0 10px #ccc;
    border-radius: 12px;
}

.card-wrap .card-body {
    padding: 1.25rem;
}

.leads-table-card {
    width: 100%;
}

.leads-table-col {
    width: 100%;
}

.leads-filters-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.leads-filter-select {
    min-width: 140px;
}

.leads-filter-select :deep(select),
.leads-filter-select :deep(.custom-select) {
    height: 34px !important;
    min-height: 34px !important;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid var(--slate-200);
    font-size: 14px;
}

.table-wrapper {
    overflow-x: auto;
    width: 100%;
    -webkit-overflow-scrolling: touch;
}

.table-border-radius {
    border-radius: 12px;
}

::v-deep .leads-table {
    width: 100%;
}

::v-deep .leads-table .table {
    width: 100%;
    min-width: 100%;
    margin-bottom: 0;
}

::v-deep .leads-table-compact thead th {
    background-color: var(--slate-50);
    border-bottom: 1px solid rgba(226, 232, 240, 0.5);
    font-weight: 600;
    color: var(--slate-700);
    padding: 10px 12px;
    font-size: 12px;
}

::v-deep .leads-table-compact tbody td {
    padding: 10px 12px;
    vertical-align: middle;
    font-size: 12px;
    line-height: 1.35;
    word-break: break-word;
    overflow-wrap: break-word;
}

::v-deep .leads-table-compact .status-badge {
    padding: 3px 8px;
    border-radius: 8px;
    font-size: 11px;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
}

.status-badge.badge-success {
    background: var(--success-bg);
    color: var(--success-text);
}

.status-badge.badge-info {
    background: var(--info-bg);
    color: var(--info-text);
}

.status-badge.badge-warning {
    background: var(--warning-bg);
    color: var(--warning-text);
}

.status-badge.badge-danger {
    background: var(--danger-bg);
    color: var(--danger-text);
}

.status-badge.badge-secondary {
    background: var(--slate-100);
    color: var(--slate-700);
}

.status-badge.badge-primary {
    background: var(--primary);
    color: #fff;
}

::v-deep .leads-table-compact .lead-name-cell {
    gap: 0.5rem;
}

::v-deep .leads-table-compact .lead-avatar {
    width: 28px;
    height: 28px;
    font-size: 0.65rem;
}

::v-deep .leads-table-compact .font-weight-semibold {
    font-size: 12px;
}

::v-deep .leads-table-compact .text-muted.small {
    font-size: 11px;
}

::v-deep .leads-table-compact .action-btn-group .button-circle {
    width: 30px;
    height: 30px;
    min-width: 30px;
    padding: 0 !important;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50% !important;
}

::v-deep .leads-table-compact .action-btn-group .button-circle i {
    margin: 0 !important;
    font-size: 12px;
    line-height: 1;
}

.lead-name-cell {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.lead-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.75rem;
    color: #fff;
    flex-shrink: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.lead-info {
    flex: 1;
    min-width: 0;
}

.font-weight-semibold {
    font-weight: 600;
    color: var(--slate-900);
}

.stats-card {
    text-align: center;
    transition: box-shadow 0.2s ease;
}

.stats-card:hover {
    box-shadow:
        0 4px 6px -1px rgba(0, 0, 0, 0.1),
        0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.stats-label {
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 0.5rem;
}

.stats-value {
    font-size: 1.75rem;
    font-weight: bold;
    color: #212529;
}

.modal-subtitle {
    font-size: 12px;
    color: var(--slate-500);
    margin: 0 0 0.75rem;
    padding: 1rem 1.5rem 0;
    text-align: center;
}

.modal-tabs-wrapper {
    display: flex;
    gap: 0.5rem;
    padding: 0 1.5rem 1rem 1.5rem;
    border-bottom: 1px solid #dee2e6;
}

.tab-button {
    border-radius: 10px;
    padding: 0.5rem 1.25rem;
    font-weight: 600;
    transition: all 0.2s;
}

.tab-button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.tab-button.active {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.tab-button:not(.active) {
    color: var(--slate-700);
}

.tab-button:not(.active):hover {
    background: var(--slate-50);
}

.modal-content-wrapper {
    padding: 1.5rem;
}

.tab-content {
    border: none !important;
    min-height: 300px;
}

.modal-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    padding: 0 1.5rem 1.25rem;
}

.modal-actions .btn {
    min-width: 140px;
}

::v-deep .add-lead-modal .modal-content {
    border-radius: 0.5rem;
}

::v-deep .add-lead-modal .modal-body {
    padding: 0;
}

::v-deep .table thead th {
    background-color: var(--slate-50);
    border-bottom: 1px solid rgba(226, 232, 240, 0.5);
    font-weight: 600;
    color: var(--slate-700);
}

::v-deep .table td {
    vertical-align: middle;
}

::v-deep .dropdown-toggle::after {
    display: none;
}

::v-deep .dropdown-toggle {
    color: #6c757d;
    font-size: 1.1rem;
}

::v-deep .dropdown-toggle:hover {
    color: #212529;
}

.form-control:disabled {
    cursor: not-allowed;
}

.pagination-wrapper {
    float: right;
    margin-top: 1rem;
}

@media (max-width: 767.98px) {
    .pagination-wrapper {
        float: none;
        text-align: center;
    }

    .leads-filters-wrapper {
        width: 100%;
    }

    .leads-filter-select {
        flex: 1 1 100%;
        min-width: 100%;
    }
}
</style>
<style lang="sass" scoped>
.action-btn-group
  display: flex
  align-items: center
  gap: 6px

.button-circle
    width: 34px
    height: 34px
    min-width: 34px
    padding: 0 !important
    margin: 0 !important
    display: flex
    align-items: center
    justify-content: center
    border-radius: 50% !important

.button-circle i
    margin: 0
    font-size: 14px
    line-height: 1

.btn-orange
  background: #fe7b08
  border: #fe7b08

.btn-green
    background-color: #28c76f !important
    border-color: #28c76f !important

.edit
  color: white !important

@media (max-width: 576px)
  .action-btn-group
    flex-direction: row
    align-items: center
    flex-wrap: wrap
    gap: 0.5rem

    .button-circle
      margin-right: 0
      margin-bottom: 0

      &:last-child
        margin-bottom: 0
</style>
